import { useEffect, useState } from "react";
import { format, isBefore, setHours, setMinutes, setSeconds } from "date-fns";
import { enUS } from "date-fns/locale";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { Box, IconButton } from "@material-ui/core";
import { uniqueId } from "lodash";

import { nextMonth, oldMonth, takeMonth } from "./datePicker.utils";
import { useStyles } from "./datePicker.styles";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

export const DatePicker = ({
  value,
  onChange,
  events
}: {
  value?: Date;
  onChange: (value: Date) => void;
  events?: { date: string; title: string }[];
}) => {
  const classes = useStyles();
  const { isMoreThanSm } = useBreakpoint();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const data = takeMonth(selectedDate || new Date())();
  const daysWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  useEffect(() => {
    if (value) {
      setSelectedDate(value);
    }
  }, [value]);

  return (
    <Box width={"100%"} className={classes.root}>
      <Box
        display={"flex"}
        width={"100%"}
        mb={isMoreThanSm ? "40px" : "25px"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <IconButton
          onClick={() => {
            const old = oldMonth(selectedDate);
            setSelectedDate(old);
          }}
        >
          <ChevronLeftIcon htmlColor="#292845" />
        </IconButton>
        <Box display={"flex"} alignItems={"center"}>
          <h2 className={classes.headerMonth}>{format(selectedDate, "MMMM", { locale: enUS })}</h2>
          <h3 className={classes.headerYear}>{format(selectedDate, "yyyy")}</h3>
        </Box>
        <IconButton
          onClick={() => {
            const next = nextMonth(selectedDate);
            setSelectedDate(next);
          }}
        >
          <ChevronRightIcon htmlColor="#292845" />
        </IconButton>
      </Box>
      <Box width={"100%"} display={"grid"} gridTemplateColumns={"repeat(7,1fr)"}>
        {daysWeek.map((dayName) => (
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            className={classes.headerWeek}
            key={uniqueId()}
          >
            {dayName}
          </Box>
        ))}
      </Box>
      <Box width={"100%"} display={"grid"} gridGap={isMoreThanSm ? "10px" : "5px"}>
        {data.map((week: any, wi: number) => (
          <Box
            display={"grid"}
            gridTemplateColumns={"repeat(7,1fr)"}
            gridGap={isMoreThanSm ? "10px" : "5px"}
            key={uniqueId()}
          >
            {week.map((day: any, di: number) => {
              const notInMonth =
                (wi === 0 && Number(format(day, "d")) > 7) ||
                (wi === data.length - 1 && Number(format(day, "d")) <= 7 && Number(format(day, "d")) >= 1);
              const isAvailable = events && events.some((item) => item.date === format(day, "yyyy/MM/dd"));
              const notSun = di !== week.length - 1;
              const notSat = di !== week.length - 2;
              const isDateSelected = value && format(day, "yyyy/MM/dd") === format(value, "yyyy/MM/dd");
              const isPast = isBefore(setSeconds(setMinutes(setHours(day, 23), 59), 59), new Date());
              const showFullText = notSun && notSat && !notInMonth && !isPast;

              const eventTitle = events && events.find((item) => item.date === format(day, "yyyy/MM/dd"))?.title;

              return (
                <Box
                  key={uniqueId()}
                  onClick={() => {
                    setSelectedDate(day);
                    onChange(day);
                  }}
                  className={`${classes.dayBox} 
                  ${isAvailable ? classes.dayBoxAvailable : ""} 
                  ${notInMonth || isPast ? classes.dayDisableBox : ""} 
                  ${isDateSelected ? classes.dayBoxActive : ""}`}
                >
                  {format(day, "d")}
                  {events ? (
                    isAvailable ? (
                      <span>{eventTitle}</span>
                    ) : showFullText ? (
                      <span>Full</span>
                    ) : null
                  ) : di === week.length - 1 && wi !== data.length - 1 ? (
                    <span>Full</span>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
